import { on, onPageScroll, prefersReducedMotion } from '@lucid/core';

const normalize = (value) =>
  value
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

class LucidFaqFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-faq-search]');
    this.items = [...this.querySelectorAll('[data-faq-item]')];
    this.groups = [...this.querySelectorAll('[data-faq-group]')];
    this.empty = this.querySelector('[data-faq-empty]');
    this.status = this.querySelector('[data-faq-status]');
    this.index = this.items.map((item) => normalize(item.textContent || ''));
    if (!this.input) return;
    this.onInput = this.onInput.bind(this);
    this.input.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
  }

  onInput() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.filter(normalize(this.input.value.trim())), 120);
  }

  filter(query) {
    const terms = query.split(/\s+/).filter(Boolean);
    let visible = 0;
    this.items.forEach((item, position) => {
      const match = terms.every((term) => this.index[position].includes(term));
      item.hidden = !match;
      if (match) visible += 1;
      if (terms.length && match && visible <= 1) item.open = true;
      if (!terms.length) item.open = false;
    });
    this.groups.forEach((group) => {
      group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
    });
    if (this.empty) this.empty.hidden = visible > 0;
    if (this.status) {
      const template = this.status.dataset.templateResults || '';
      this.status.textContent = terms.length ? template.replace('[n]', String(visible)) : '';
    }
  }
}

const INCH = 2.54;

class LucidSizeTable extends HTMLElement {
  connectedCallback() {
    this.cells = [...this.querySelectorAll('[data-size-value]')];
    this.cells.forEach((cell) => {
      cell.dataset.cm = cell.textContent.trim();
    });
    this.note = this.querySelector('[data-unit-note]');
    this.decimal = (document.documentElement.lang || 'de').startsWith('en') ? '.' : ',';
    this.off = on('segmented:change', (event) => {
      if (event.detail?.name !== this.dataset.unitGroup) return;
      this.apply(event.detail.value);
    });
  }

  disconnectedCallback() {
    this.off?.();
  }

  convert(text) {
    return text.replace(/\d+(?:[.,]\d+)?/g, (match) => {
      const inches = Number.parseFloat(match.replace(',', '.')) / INCH;
      const rounded = Math.round(inches * 10) / 10;
      return String(rounded).replace('.', this.decimal);
    });
  }

  apply(unit) {
    this.cells.forEach((cell) => {
      const source = cell.dataset.cm || '';
      cell.textContent = unit === 'in' ? this.convert(source) : source;
    });
    if (this.note) {
      this.note.textContent = unit === 'in' ? this.note.dataset.noteIn : this.note.dataset.noteCm;
    }
  }
}

class LucidReadingProgress extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-progress-bar]');
    this.target = document.getElementById(this.dataset.target || '');
    if (!this.bar || !this.target) return;
    this.update = this.update.bind(this);
    this.schedule = () => {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    };
    this.offScroll = onPageScroll(this.schedule);
    window.addEventListener('resize', this.schedule, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    this.offScroll?.();
    this.offScroll = null;
    window.removeEventListener('resize', this.schedule);
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }

  update() {
    this.frame = null;
    const rect = this.target.getBoundingClientRect();
    const distance = rect.height - window.innerHeight;
    const progress = distance > 0 ? Math.min(1, Math.max(0, -rect.top / distance)) : rect.top < 0 ? 1 : 0;
    this.bar.style.transform = `scaleX(${progress.toFixed(4)})`;
    this.toggleAttribute('data-active', rect.top < 0 && rect.bottom > 0);
  }
}

class LucidShare extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.button = this.querySelector('[data-share-native]');
    this.copy = this.querySelector('[data-share-copy]');
    this.status = this.querySelector('[data-share-status]');
    const url = this.dataset.url || window.location.href;
    const title = this.dataset.title || document.title;

    if (this.button && typeof navigator.share === 'function') {
      this.button.hidden = false;
      this.button.addEventListener(
        'click',
        () => {
          navigator.share({ title, url }).catch(() => {});
        },
        { signal }
      );
    }

    this.copy?.addEventListener(
      'click',
      async () => {
        try {
          await navigator.clipboard.writeText(url);
          this.announce(this.copy.dataset.copied || '');
        } catch (error) {
          this.announce(url);
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    window.clearTimeout(this.timer);
  }

  announce(text) {
    if (!this.status) return;
    this.status.textContent = text;
    this.toggleAttribute('data-copied', true);
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(
      () => {
        this.removeAttribute('data-copied');
        this.status.textContent = '';
      },
      prefersReducedMotion() ? 4000 : 2400
    );
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('lucid-faq-filter', LucidFaqFilter);
define('lucid-size-table', LucidSizeTable);
define('lucid-reading-progress', LucidReadingProgress);
define('lucid-share', LucidShare);
